import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import '../HomePage.css';
import './FeaturesPage.css';
import googlePlayImage from '../assets/images/google-play.png';
import appleStoreImage from '../assets/images/apple-store.png';
import exploreScreenPortrait from '../assets/images/explore-screen-portrait.png';
import consiliumVantageToday from '../assets/images/consilium-vantage-today.png';
import crimeAlertsPortrait from '../assets/images/crime-alerts-portrait.png';
import environmentalAlertsPortrait from '../assets/images/envrironmental-alerts-portrait.png';
import neighborhoodWatchPortrait from '../assets/images/neighborhood-watch-portrait.png';
import sentinelPortrait from '../assets/images/sentinel-portrait.png';
import healthAlertsPortrait from '../assets/images/health-alerts-portrait.png';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { onAppStoreClick } from '../utils/metaPixel';

const GOOGLE_PLAY_URL = process.env.REACT_APP_GOOGLE_PLAY_URL ?? 'https://play.google.com/store/apps/details?id=com.safescope.app&utm_source=na_Med';
const APP_STORE_URL = 'https://apps.apple.com/us/app/consilium-safety/id6760517718';

type FeatureTier = 'standard' | 'premium' | 'mixed' | 'method';

type FeaturePage = {
  id: string;
  name: string;
  tagline: string;
  eyebrow: string;
  title: string;
  summary: string;
  tier: FeatureTier;
  mockTitle: string;
  mockState: string;
  mockRows: Array<{ label: string; value: string }>;
  heroImage?: string;
  heroAlt?: string;
  whyHeading?: string;
  valueProps: Array<{ title: string; body: string }>;
  howHeading?: string;
  howItWorks: Array<{ title: string; body: string }>;
  standard: string;
  premium: string;
  cta: string;
};

const featurePages: FeaturePage[] = [
  {
    id: 'vantage',
    name: 'Consilium Vantage',
    tagline: 'Executive area intelligence',
    eyebrow: 'Consilium Vantage',
    title: 'The one briefing that tells you what actually changed.',
    summary:
      'Consilium Vantage is the app\'s editorial intelligence layer: a calm daily and weekly readout of what changed, what did not, and what it means for the places you track.',
    tier: 'premium',
    mockTitle: 'Sammamish, WA',
    mockState: 'Quiet today',
    mockRows: [
      { label: 'Edition', value: 'Consilium Vantage Today' },
      { label: 'Readout', value: 'Seasonal vehicle break-ins noted' },
      { label: 'Action', value: 'Stay aware, no urgent change' },
    ],
    heroImage: consiliumVantageToday,
    heroAlt: 'Consilium Vantage Today activity screen inside the Consilium app',
    valueProps: [
      { title: 'Signal over noise', body: 'Consilium Vantage reads across crime, health, environmental, community, and monitoring signals before asking for your attention.' },
      { title: 'One report is not a pattern', body: 'A single event stays framed as a single event unless the evidence supports a broader trend.' },
      { title: 'Quiet is still useful', body: 'On calm days, Consilium Vantage explains why conditions look steady instead of leaving you with an empty feed.' },
    ],
    howItWorks: [
      { title: 'Daily read, weekly recap', body: 'A short Consilium Vantage Today brief when something matters, plus a weekly recap that shows the throughline.' },
      { title: 'Sourced, scored, explained', body: 'Briefs draw from the same intelligence layers that power the rest of Consilium.' },
      { title: 'Current first, context second', body: 'Consilium Vantage leads with today’s evidence, then uses older, seasonal, or historical signals as context.' },
    ],
    standard: 'A short overview of what is happening in the city.',
    premium: 'Full daily brief, weekly recap, and the reasoning behind every callout.',
    cta: 'Stop guessing what changed. Let Consilium Vantage tell you.',
  },
  {
    id: 'health-intelligence',
    name: 'Health Intelligence',
    tagline: 'Signals, trends, scoring, outbreaks',
    eyebrow: 'Health Intelligence',
    title: 'Know what is moving through your area before it becomes a headline.',
    summary:
      'Consilium reads local health signals, advisories, food and water safety, and disease activity, then turns them into a clear score and trend.',
    tier: 'mixed',
    mockTitle: 'Travis County, TX',
    mockState: 'Health Score 81',
    mockRows: [
      { label: 'Trend', value: 'Steady vs last week' },
      { label: 'Activity', value: 'Respiratory and foodborne signals' },
      { label: 'Context', value: 'Present, monitored' },
    ],
    heroImage: healthAlertsPortrait,
    heroAlt: 'Consilium health alerts screen showing local health signals',
    valueProps: [
      { title: 'Numbers alone mislead', body: 'Raw signal counts are not the same as official case tallies, so Consilium presents direction and context instead.' },
      { title: 'Early, local, human', body: 'Health signals often appear locally through advisories, local reporting, and nearby observations before they are national stories.' },
      { title: 'Calm by design', body: 'Health Intelligence informs without inflating uncertainty into panic.' },
    ],
    howItWorks: [
      { title: 'Health Score', body: 'A continuously refreshed read on health conditions in your tracked area.' },
      { title: 'Trend, not false precision', body: 'Week-over-week direction shows whether signals are rising, steady, or easing.' },
      { title: 'Disease and outbreak awareness', body: 'Connected to Consilium Sentinel disease intelligence, with observed and forecast risk kept separate.' },
    ],
    standard: 'Health Score and current status for one tracked region.',
    premium: 'Score-change explanations, seven-day trend charting, roaming coverage, and deeper outbreak context.',
    cta: 'See what is changing in the people, water, and air around you.',
  },
  {
    id: 'crime-intelligence',
    name: 'Crime Intelligence',
    tagline: 'Signals, trends, scoring, local patterns',
    eyebrow: 'Crime Intelligence',
    title: 'Know what crime activity is actually changing near you.',
    summary:
      'Consilium turns local crime signals into a clear score, an honest trend, and pattern detection that only speaks up when the evidence earns it.',
    tier: 'mixed',
    mockTitle: 'Downtown Denver, CO',
    mockState: 'Crime Score 64',
    mockRows: [
      { label: 'Trend', value: 'Up modestly vs last week' },
      { label: 'Property crime', value: 'Rising' },
      { label: 'Violent crime', value: 'Steady' },
    ],
    heroImage: crimeAlertsPortrait,
    heroAlt: 'Consilium crime alerts screen showing local crime intelligence',
    whyHeading: 'Crime context without the panic.',
    valueProps: [
      { title: 'Clear about what it knows', body: 'Consilium separates reported signals, nearby context, and confirmed patterns so one piece of information does not get overstated.' },
      { title: 'One event is not a wave', body: 'A single report does not become a trend. Consilium waits for independent evidence before elevating activity as a pattern.' },
      { title: 'Local means local', body: 'Nearby activity can add context, but it stays labeled as nearby and does not get blended into your area’s score.' },
    ],
    howHeading: 'From score to signal to map.',
    howItWorks: [
      { title: 'Crime Score', body: 'Consilium combines baseline area risk with recent crime signals to show whether conditions look steady, elevated, or improving.' },
      { title: 'Pattern detection', body: 'When multiple independent signals point to a repeated issue, Consilium surfaces the category and context without turning one report into a trend.' },
      { title: 'See the map behind the score', body: 'Premium users can move from the summary to the regional map, making it easier to understand where crime activity is showing up and what deserves attention.' },
    ],
    standard: 'Crime Score, trend chip, and top signals for your area.',
    premium: 'Interactive map, seven-day trend charting, and deeper pattern breakdowns.',
    cta: 'See the pattern, not just the panic.',
  },
  {
    id: 'environmental-intelligence',
    name: 'Environmental Intelligence',
    tagline: 'Air, water, smoke, spills, scoring',
    eyebrow: 'Environmental Intelligence',
    title: 'Air, water, and weather-driven risk in plain language.',
    summary:
      'From wildfire smoke to water advisories and spill reports, Consilium scores environmental signals so you know when conditions are actually worth changing plans for.',
    tier: 'mixed',
    mockTitle: 'Boise, ID',
    mockState: 'Environmental Score 57',
    mockRows: [
      { label: 'Trend', value: 'Elevated smoke advisory' },
      { label: 'Air quality', value: 'Elevated' },
      { label: 'Water and soil', value: 'Steady' },
    ],
    heroImage: environmentalAlertsPortrait,
    heroAlt: 'Consilium environmental alerts screen showing local environmental intelligence',
    whyHeading: 'Your environment changes before the headlines catch up.',
    valueProps: [
      { title: 'A wider local radar', body: 'Consilium watches across air, water, weather, spills, advisories, and hazard reports so you are not limited to whatever happens to make the news.' },
      { title: 'One clear local read', body: 'Environmental information is scattered across agencies, alert systems, and public reports. Consilium brings it together into a single score and plain-language context for the places you care about.' },
      { title: 'Useful without alarm', body: 'Consilium helps you understand when local conditions deserve attention and when they are simply background context, so you can adjust without overreacting.' },
    ],
    howHeading: 'From scattered signals to one local read.',
    howItWorks: [
      { title: 'Environmental Score', body: 'Consilium brings air, water, weather, spill, advisory, and hazard signals into a simple read on local environmental conditions.' },
      { title: 'Always watching the local picture', body: 'Consilium keeps looking for environmental changes in the places you care about, whether or not they are big enough to become headlines.' },
      { title: 'Ground-level context', body: 'Community reports can add what people are actually seeing, smelling, or experiencing nearby, giving the score more local texture.' },
    ],
    standard: 'Environmental Score and top signals for your area.',
    premium: 'Trend charting, component breakdowns, and roaming coverage that follows you across regions.',
    cta: 'Know before the smoke rolls in.',
  },
  {
    id: 'explore',
    name: 'Explore',
    tagline: 'Map-based intelligence for monitored regions',
    eyebrow: 'Explore',
    title: 'See what is happening across the regions you follow.',
    summary:
      'Explore turns local crime and environmental signals into a map view for the regions you monitor, so scattered activity becomes easier to understand.',
    tier: 'mixed',
    mockTitle: 'Asheville, NC',
    mockState: 'Explore result',
    mockRows: [
      { label: 'Health', value: '74' },
      { label: 'Crime', value: '68' },
      { label: 'Environment', value: '80' },
    ],
    heroImage: exploreScreenPortrait,
    heroAlt: 'Consilium Explore screen showing a regional map of local intelligence',
    whyHeading: 'Your region becomes visible.',
    valueProps: [
      { title: 'See activity on the map', body: 'Explore turns crime and environmental signals for the region you follow into a map, so you can see where activity is showing up instead of reading disconnected alerts.' },
      { title: 'Built around the places you monitor', body: 'Switch between the regions you follow and keep the picture tied to your actual watchlist, not a generic search experience.' },
      { title: 'Context at a tap', body: 'Markers give you quick summaries of nearby activity, helping you understand what deserves attention without digging through scattered updates.' },
    ],
    howHeading: 'A map view for the places you monitor.',
    howItWorks: [
      { title: 'Choose a region', body: 'Use your monitored regions to move the map between the places you care about, including your current area when location is enabled.' },
      { title: 'See signals in place', body: 'Explore plots recent crime and environmental activity on the map, making it easier to understand where things are happening.' },
      { title: 'Tap for context', body: 'Open a marker for a quick summary of the activity behind it, so the map gives you meaning, not just pins.' },
    ],
    standard: 'Explore is a Premium feature.',
    premium: 'Map-based intelligence for monitored regions, with recent crime and environmental signals shown in place.',
    cta: 'Wherever you are headed, look before you land.',
  },
  {
    id: 'community-reports',
    name: 'Community Reports',
    tagline: 'Submit observations, strengthen the signal',
    eyebrow: 'Community Reports',
    title: 'Your neighborhood sees what headlines miss.',
    summary:
      'Community Reports gives residents a structured way to share local crime, health, and environmental observations, adding ground-level context while keeping community input clearly labeled.',
    tier: 'standard',
    mockTitle: 'Local reports',
    mockState: 'Community-derived',
    mockRows: [
      { label: 'Crime', value: 'Car break-ins near Elm and 4th' },
      { label: 'Environmental', value: 'Smoke smell drifting into Riverside' },
      { label: 'Trust', value: 'Votes, flags, and Signal Score' },
    ],
    heroImage: neighborhoodWatchPortrait,
    heroAlt: 'Consilium screen showing local community and neighborhood watch activity',
    whyHeading: 'A better way to share what people are seeing.',
    valueProps: [
      { title: 'Local eyes, structured reports', body: 'Neighbors can share crime, health, and environmental observations before they appear in official channels, local news, or broader alert systems.' },
      { title: 'Community input with guardrails', body: 'Reports stay clearly labeled, and voting and flagging help useful context rise while rumor and low-quality posts stay in check.' },
      { title: 'Participation that matters', body: 'Your reports and votes strengthen the local picture for everyone nearby, turning community awareness into something useful.' },
    ],
    howItWorks: [
      { title: 'Submit in under a minute', body: 'Choose a category, add details and location, then decide whether to post publicly or anonymously.' },
      { title: 'Vote, flag, validate', body: 'Community and moderation checks work together to keep quality high.' },
      { title: 'Report on alerts', body: 'Add neighbor context to an existing Consilium signal without changing its official status.' },
    ],
    standard: 'Browsing and submitting Community Reports is available to every Consilium user.',
    premium: 'An ad-free community feed plus the ability to create and manage Neighborhood Watch groups.',
    cta: 'Add what you know. Someone nearby needs it.',
  },
  {
    id: 'neighborhood-watch',
    name: 'Neighborhood Watch',
    tagline: 'Trusted-circle coordination',
    eyebrow: 'Neighborhood Watch',
    title: 'Your block, your building, your people, one channel.',
    summary:
      'Neighborhood Watch turns Community Reports into a private coordination space for the people you actually trust.',
    tier: 'premium',
    mockTitle: 'Maple Street Watch',
    mockState: 'Private group',
    mockRows: [
      { label: 'Members', value: '14' },
      { label: 'Recent report', value: 'Package theft near #212' },
      { label: 'Mode', value: 'Group-only sharing' },
    ],
    heroImage: neighborhoodWatchPortrait,
    heroAlt: 'Consilium Neighborhood Watch screen showing local coordination features',
    whyHeading: 'Keep local coordination where it belongs.',
    valueProps: [
      { title: 'Kept where it is useful', body: 'Hyperlocal chatter can stay with the people who need it instead of broadcasting citywide.' },
      { title: 'Coordination, not just alerts', body: 'Neighbors, HOAs, parent groups, and small teams can respond in one place.' },
      { title: 'Trust rules still apply', body: 'Watch groups inherit Community Reports moderation and privacy standards.' },
    ],
    howHeading: 'Turn local concern into coordinated action.',
    howItWorks: [
      { title: 'Start a watch for your real-world group', body: 'Create a dedicated space for your block, building, HOA, school group, or team, tied to the area you care about.' },
      { title: 'Bring in the right people', body: 'Invite neighbors and trusted contacts so updates reach the people who can actually help, not the whole internet.' },
      { title: 'Coordinate without broadcasting', body: 'Share reports with your group first, or alongside the public feed, keeping sensitive local context in the right hands.' },
    ],
    standard: 'Standard users can be invited into and participate in a Neighborhood Watch group.',
    premium: 'Premium is required to create and administer a Neighborhood Watch group.',
    cta: 'Bring your block online.',
  },
  {
    id: 'sentinel',
    name: 'Consilium Sentinel',
    tagline: 'Proactive, always-on intelligence',
    eyebrow: 'Consilium Sentinel',
    title: 'Always-on intelligence for risks you should not have to chase.',
    summary:
      'Consilium Sentinel is the app\'s proactive intelligence engine, modeling crime pattern risk and disease spread risk in the background.',
    tier: 'premium',
    mockTitle: 'Consilium Sentinel Monitor',
    mockState: 'Observed vs forecast',
    mockRows: [
      { label: 'Crime model', value: 'Risk elevated' },
      { label: 'Disease spread', value: 'Low forecast risk' },
      { label: 'Watch items', value: 'Quiet unless evidence clears the bar' },
    ],
    heroImage: sentinelPortrait,
    heroAlt: 'Consilium Sentinel screen showing proactive monitoring',
    whyHeading: 'Earlier awareness, without the noise.',
    valueProps: [
      { title: 'A head start on emerging risk', body: 'Consilium Sentinel looks for crime and health patterns before they become obvious, giving you earlier context without forcing you to monitor everything yourself.' },
      { title: 'Prediction with boundaries', body: 'Observed activity and forecast risk stay separate, so you get forward-looking intelligence without pretending the model can see the future.' },
      { title: 'Quiet until it matters', body: 'Most monitored activity never interrupts you. Consilium Sentinel only raises concerns when the evidence clears the bar.' },
    ],
    howHeading: 'Early intelligence from patterns others miss.',
    howItWorks: [
      { title: 'Crime risk, modeled from local patterns', body: 'Consilium Sentinel looks at recent crime activity, regional history, trend changes, and threat chatter to identify risks that may deserve attention.' },
      { title: 'Disease awareness before certainty', body: 'Health Sentinel tracks local and regional disease evidence, spread potential, and outbreak forecast signals without turning media volume or raw case counts into public claims.' },
      { title: 'Watch items, not false alarms', body: 'When something may matter but does not yet clear the bar for a forecast or alert, Consilium Sentinel keeps it in view as monitoring context.' },
    ],
    standard: 'Consilium Sentinel is not included in the free tier.',
    premium: 'Proactive crime and health intelligence, paired with roaming and multi-region monitoring.',
    cta: 'Let something else stay up at night.',
  },
  {
    id: 'disease-case-count-tracking',
    name: 'Disease Case Count Tracking',
    tagline: 'The method behind health trends',
    eyebrow: 'Health Methodology',
    title: 'The disease counts no one else is keeping current.',
    summary:
      'For many tracked diseases, there is no single official, up-to-date national count people can rely on. Consilium reviews reported numbers from local coverage and official health sources, removes repeats and stale totals, and uses only qualified evidence to help shape Health Score trends.',
    tier: 'method',
    mockTitle: 'Case count review',
    mockState: 'Reported activity increasing',
    mockRows: [
      { label: 'Repeated totals', value: 'Never summed' },
      { label: 'Effective date', value: 'Preserved' },
      { label: 'Stale data', value: 'Decays over time' },
    ],
    heroImage: healthAlertsPortrait,
    heroAlt: 'Consilium health alerts screen showing disease and public health signals',
    whyHeading: 'Scattered disease numbers need discipline.',
    valueProps: [
      { title: 'One count, not ten echoes', body: 'Local stories and official updates often repeat the same running total. Consilium treats repeated numbers as one piece of evidence, not a surge.' },
      { title: 'Freshness changes meaning', body: 'A number is only useful if we know when it was true. Consilium preserves the effective date so old counts do not look like new activity.' },
      { title: 'Geography matters', body: 'County, state, and national counts stay in their proper scope, adding context without creating false local alarms.' },
    ],
    howHeading: 'From scattered reports to responsible trends.',
    howItWorks: [
      { title: 'Find and verify reported counts', body: 'Consilium reviews numbers from local coverage and official health sources, then checks for repeats, dates, and geographic scope before they can influence the product.' },
      { title: 'Shape direction, not panic counters', body: 'Reported counts help Consilium understand whether disease activity appears to be rising, steady, or easing. Users see qualitative trends, not fragile live totals.' },
      { title: 'Let old evidence age out', body: 'When reporting slows, Consilium does not pretend a disease vanished or keep old numbers alive forever. Evidence fades based on recency and disease behavior.' },
    ],
    standard: 'Reviewed disease trend context can inform the short overview Standard users see when it is relevant.',
    premium: 'Reviewed disease trends power deeper Health Intelligence, Consilium Vantage, and Consilium Sentinel disease intelligence.',
    cta: 'See the trend, not the noise.',
  },
];

const tierLabel = (tier: FeatureTier): string => {
  if (tier === 'standard') return 'Standard';
  if (tier === 'premium') return 'Premium';
  if (tier === 'method') return 'Methodology';
  return 'Standard + Premium';
};

const AppDownloadButtons: React.FC<{ placement: string }> = ({ placement }) => (
  <div className="store-buttons features-store-buttons" aria-label="Download Consilium">
    <a
      href={GOOGLE_PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button google"
      aria-label="Get Consilium on Google Play"
      onClick={onAppStoreClick('android', { placement })}
    >
      <img src={googlePlayImage} alt="Get it on Google Play" className="store-icon" />
    </a>
    <a
      href={APP_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-button apple"
      aria-label="Download Consilium on the App Store"
      onClick={onAppStoreClick('ios', { placement })}
    >
      <img src={appleStoreImage} alt="Download on the App Store" className="store-icon" />
    </a>
  </div>
);

const FeatureMock: React.FC<{ feature: FeaturePage }> = ({ feature }) => (
  <aside className="features-mock-card" aria-label={`${feature.name} sample`}>
    <div className="features-mock-header">
      <span>{feature.mockTitle}</span>
      <small>{feature.mockState}</small>
    </div>
    <div className={`features-score-orb features-score-${feature.tier}`}>
      {feature.tier === 'premium' ? 'P' : feature.tier === 'method' ? 'M' : 'S'}
    </div>
    <div className="features-mock-rows">
      {feature.mockRows.map(({ label, value }) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  </aside>
);

const FeatureScreenshotHero: React.FC<{ feature: FeaturePage }> = ({ feature }) => (
  <aside className="features-product-stage" aria-label={`${feature.name} product screenshot`}>
    {feature.id === 'vantage' ? (
      <div className="features-device-frame">
        <div className="features-device-island" aria-hidden="true" />
        <img src={feature.heroImage} alt={feature.heroAlt ?? `${feature.name} app screen`} />
      </div>
    ) : (
      <img
        className="features-product-image"
        src={feature.heroImage}
        alt={feature.heroAlt ?? `${feature.name} app screen`}
      />
    )}
  </aside>
);

const FeatureHeroVisual: React.FC<{ feature: FeaturePage }> = ({ feature }) => {
  if (feature.heroImage) {
    return <FeatureScreenshotHero feature={feature} />;
  }

  return <FeatureMock feature={feature} />;
};


const FeaturesHub: React.FC = () => (
  <>
    <header className="features-hero">
      <div>
        <div className="features-eyebrow">Consilium Features</div>
        <h1>Every signal, scored and explained.</h1>
        <p>
          Consilium gives you one place to understand what matters near you. Instead of piecing together news,
          social posts, neighborhood chatter, and secondhand warnings, you get crime, health, environmental,
          community, and proactive intelligence filtered for relevance.
        </p>
        <div className="features-actions">
          <a href="#feature-grid" className="features-primary-link">Explore features</a>
          <a href="#download-consilium" className="features-secondary-link">Download Consilium</a>
        </div>
      </div>
      <div className="features-hero-screenshot">
        <div className="features-hero-screenshot-copy">
          <span>One Place</span>
          <strong>Crime, health, environment, and community signals together.</strong>
        </div>
        <img src={exploreScreenPortrait} alt="Consilium Explore screen showing local intelligence" />
      </div>
    </header>

    <section className="features-grid-section" id="feature-grid">
      <div className="features-section-heading">
        <div className="features-eyebrow">Feature Pages</div>
        <h2>Choose the capability you want to understand.</h2>
      </div>
      <div className="features-card-grid">
        {featurePages.map(feature => (
          <Link to={`/features/${feature.id}`} className="features-card" key={feature.id}>
            <span className={`features-tier features-tier-${feature.tier}`}>{tierLabel(feature.tier)}</span>
            <h3>{feature.name}</h3>
            <p>{feature.tagline}</p>
          </Link>
        ))}
      </div>
    </section>
  </>
);

const FeatureDetail: React.FC<{ feature: FeaturePage }> = ({ feature }) => (
  <>
    <div className="features-breadcrumb">
      <Link to="/features">Features</Link>
      <span>/</span>
      <span>{feature.name}</span>
    </div>

    <header className="features-detail-hero">
      <div>
        <div className="features-eyebrow">{feature.eyebrow}</div>
        <h1>{feature.title}</h1>
        <p>{feature.summary}</p>
        <div className="features-actions">
          <a href="#download-consilium" className="features-primary-link">Download Consilium</a>
          <Link to="/features" className="features-secondary-link">All features</Link>
        </div>
      </div>
      <FeatureHeroVisual feature={feature} />
    </header>

    <section className="features-detail-section">
      <div className="features-section-heading">
        <div className="features-eyebrow">Why It Matters</div>
        <h2>{feature.whyHeading ?? 'Built for calm decisions, not attention capture.'}</h2>
      </div>
      <div className="features-value-grid">
        {feature.valueProps.map(({ title, body }) => (
          <article key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="features-detail-section">
      <div className="features-section-heading">
        <div className="features-eyebrow">How It Works</div>
        <h2>{feature.howHeading ?? 'The product behavior behind the promise.'}</h2>
      </div>
      <div className="features-how-list">
        {feature.howItWorks.map(({ title, body }) => (
          <article key={title}>
            <h3>{title}</h3>
            <p>{body}</p>
          </article>
        ))}
      </div>
    </section>

    <section className="features-tier-card">
      <div>
        <div className="features-eyebrow">Standard vs Premium</div>
        <h2>What is included, plainly.</h2>
      </div>
      <div className="features-tier-columns">
        <article>
          <span>Standard</span>
          <p>{feature.standard}</p>
        </article>
        <article>
          <span>Premium</span>
          <p>{feature.premium}</p>
        </article>
      </div>
    </section>

    <section className="features-cta-card" id="download-consilium">
      <div>
        <div className="features-eyebrow">Download Consilium</div>
        <h2>{feature.cta}</h2>
        <p>Start with the free app, then upgrade inside Consilium when you want deeper context and Premium features.</p>
      </div>
      <AppDownloadButtons placement={`features-${feature.id}`} />
    </section>
  </>
);

const FeaturesPage: React.FC = () => {
  const { featureId } = useParams();
  const feature = featureId ? featurePages.find(item => item.id === featureId) : null;

  if (featureId && !feature) {
    return <Navigate to="/features" replace />;
  }

  return (
    <div className="features-page-wrapper">
      <Header downloadHref="#download-consilium" />
      <main className="features-page">
        {feature ? <FeatureDetail feature={feature} /> : <FeaturesHub />}
      </main>
      {!feature && (
        <section className="features-cta-card features-hub-cta" id="download-consilium">
          <div>
            <div className="features-eyebrow">Download Consilium</div>
            <h2>Get the signal without the noise.</h2>
            <p>Download Consilium free on Android and iOS, then choose Premium when you are ready for deeper intelligence.</p>
          </div>
          <AppDownloadButtons placement="features-hub" />
        </section>
      )}
      <Footer />
    </div>
  );
};

export default FeaturesPage;
